import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { crudStatusEnum } from '../../../../constants';
import { useCreateQuarryMutation, useUpdateQuarryMutation } from '../../../../hooks/mutations/useQuarryMutation';
import { useAuth } from '../../../../hooks/useAuth';
import { Quarry } from '../../../../services/apiSLTP/sites/quarries';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import CustomTextInput from '../../../ui/CustomTextInput';
import ToggleLine from '../../../ui/ToggleLine';
import ModalBody from '../../ModalBody';

interface ModalProps {
    quarry: Quarry | null;
    isEditMode?: boolean;
    showEditModal: boolean;
    setShowEditModal: React.Dispatch<React.SetStateAction<boolean>>;
    refetch: () => void;
}

const UpdateQuarryModal: React.FC<ModalProps> = ({ quarry, isEditMode = true, showEditModal, setShowEditModal, refetch }) => {
    const { t } = useTranslation();

    const [designation, setDesignation] = useState(quarry?.designation || '');
    const [selectedAddress, setSelectedAddress] = useState(quarry?.address || '');
    const [crudStatusEnumState, setCrudStatusState] = useState(quarry?.crudStatus || crudStatusEnum.OK);

    const { user } = useAuth();

    const {
        mutate: mutateQuarry,
        isSuccess: isSuccessQuarry,
        isError: isErrorQuarry,
        isLoading: isLoadingQuarry,
        error: errorQuarry,
        data: dataQuarry,
    } = useUpdateQuarryMutation({
        onSuccess: (data, variables) => {
            refetch();
        },
        onError: (error) => {
            console.error('Error updating quarry:', error.message);
        },
    });

    const {
        mutate: addQuarry,
        isSuccess: isSuccessAdded,
        isError: isErrorAdded,
        isLoading: isLoadingAdded,
        error: errorAdded,
        data: dataAdded,
    } = useCreateQuarryMutation({
        onSuccess: (data, variables) => {
            refetch();
        },
        onError: (error) => {
            console.error('Error adding quarry:', error.message);
        },
    });

    const handleDesignationChange = (text: string) => {
        setDesignation(text);
    };

    const handleActiveChange = (isActive: boolean) => {
        setCrudStatusState(isActive ? crudStatusEnum.OK : crudStatusEnum.INACTIVE);
    };

    return (
        <ModalBody
            title={t('quarry')}
            subtitle={isEditMode ? t('update') : t('add')}
            open={showEditModal}
            handleClose={() => setShowEditModal(false)}
            disableSubmit={!designation.length}
            onSubmit={() => {
                if (isEditMode) {
                    if (!quarry) return;
                    mutateQuarry({
                        quarryId: quarry._id,
                        data: {
                            designation,
                            crudStatus: crudStatusEnumState,
                            address: selectedAddress,
                        },
                    });
                } else {
                    addQuarry({ designation, address: selectedAddress, crudStatus: crudStatusEnumState, createdBy: user?._id });
                }
            }}
        >
            <CustomTextInput value={designation} label={t('name')} onChange={handleDesignationChange} required />

            <ToggleLine
                checked={crudStatusEnumState === crudStatusEnum.OK}
                onChange={handleActiveChange}
                title={capitalizeFirstLetter(t('active'))}
            />

            <CustomTextInput value={selectedAddress} label={t('address')} onChange={(text) => setSelectedAddress(text)} />
        </ModalBody>
    );
};

export default UpdateQuarryModal;
